import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useDeleteVehicleClassMutation,
  usePostVehicleClassSetAsActiveMutation,
  usePostVehicleClassSetAsInactiveMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function WorkshopVehicleClasses() {
  const navigate = useNavigate();

  const [activateVehicleClass] = usePostVehicleClassSetAsActiveMutation();
  const [deactivateVehicleClass] = usePostVehicleClassSetAsInactiveMutation();
  const [deleteVehicleClass] = useDeleteVehicleClassMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', 'edit', () => {
        navigate(composePath(settingsRoutes.workshopVehicleClassDetail, {params: {id}}));
      })
      .with('setAsActive', () => {
        activateVehicleClass({vehicleClassId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('setAsInactive', () => {
        deactivateVehicleClass({vehicleClassId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('delete', () => {
        openDeleteDialog({
          onConfirm: () => {
            deleteVehicleClass({vehicleClassId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError);
          },
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.workshop.labels.vehicleClasses'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.workshop.actions.newVehicleClass'),
        onClick: () => navigate(settingsRoutes.workshopVehicleClassCreate),
      },
    ],
  };

  return (
    <>
      <Helmet title={i18n.t('entity.workshop.labels.vehicleClasses')} />
      <SettingsTemplate
        description={i18n.t('entity.workshop.labels.vehicleClassesDescription')}
        data-testid={testIds.settings.workshopVehicleClasses('template')}
        header={header}
      >
        <DmsDataGrid gridCode="vehicle-class" actionCallback={actionCallback} />
      </SettingsTemplate>
    </>
  );
}
